import { useTpmDataFromBusinessPersonData } from '@/context/BusinessPersonData';
import { TPMData } from '@/context/services/tpm/type';
import {
  useHasSpecialExceptionWithControl,
  useReportForm,
} from '@/features/InvoiceReport/Edit/components/hooks/hooks';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useCallback } from 'react';
import { Control, useFieldArray, useFormContext } from 'react-hook-form';

export const useChangeInvoiceKind = (
  control: Control<PaymentRequestForm>,
  isDutyFree: boolean
) => {
  const changeInvoiceKindByHasSpecialExceptionStatus =
    useChangeInvoiceKindByHasSpecialExceptionStatus(control, isDutyFree);
  const changeInvoiceKindByInvoiceRegistrationNumber =
    useChangeInvoiceKindByInvoiceRegistrationNumber(control, isDutyFree);
  return {
    changeInvoiceKindByHasSpecialExceptionStatus,
    changeInvoiceKindByInvoiceRegistrationNumber,
  };
};

export const useChangeInvoiceKindByHasSpecialExceptionStatus = (
  control: Control<PaymentRequestForm>,
  isDutyFree: boolean
) => {
  const tpmData = useTpmDataFromBusinessPersonData();
  const { updateInvoiceKind } = useReportForm();
  const { getValues } = useFormContext<PaymentRequestForm>();
  const { replace } = useFieldArray<PaymentRequestForm, 'invoiceTransactions'>({
    control: control,
    name: 'invoiceTransactions',
  });

  return useCallback(
    (hasSpecialExceptionStatusId: boolean) => {
      const invoices = getValues('invoiceTransactions').map((invoice) =>
        updateInvoiceKind(
          invoice,
          hasSpecialExceptionStatusId,
          isDutyFree,
          tpmData
        )
      );
      replace(invoices);
    },
    [getValues, isDutyFree, replace, tpmData, updateInvoiceKind]
  );
};

export const useChangeInvoiceKindByInvoiceRegistrationNumber = (
  control: Control<PaymentRequestForm>,
  isDutyFree: boolean
) => {
  const tpmStateValue = useTpmDataFromBusinessPersonData();
  const { updateInvoiceKind } = useReportForm();

  const { getValues, setValue } = useFormContext<PaymentRequestForm>();

  const hasSpecialException = useHasSpecialExceptionWithControl(control);
  return useCallback(
    async (tpmValue?: TPMData) => {
      getValues('invoiceTransactions').forEach((invoice, idx) => {
        const newInvoice = updateInvoiceKind(
          invoice,
          hasSpecialException,
          isDutyFree,
          tpmValue || tpmStateValue
        );
        setValue(`invoiceTransactions.${idx}`, newInvoice);
      });
    },
    [
      getValues,
      updateInvoiceKind,
      hasSpecialException,
      isDutyFree,
      tpmStateValue,
      setValue,
    ]
  );
};
